import type { RDFTerm } from "../sparql-view/types"

export type PrefixMap = Map<string, string>

const XSD_STRING = "http://www.w3.org/2001/XMLSchema#string"

/**
 * Abbreviate a URI using provided prefixes, falling back to common prefixes.
 * Returns pre-escaped HTML with the prefix wrapped in a uri-prefix span.
 */
function abbreviateUri(uri: string, prefixes?: PrefixMap): string {
  // Try provided prefixes first
  if (prefixes) {
    for (const [prefix, namespace] of prefixes) {
      if (uri.startsWith(namespace)) {
        const localName = uri.slice(namespace.length)
        return `<span class="uri-prefix">${escapeHtml(prefix)}:</span>${escapeHtml(localName)}`
      }
    }
  }

  // Fall back to hardcoded common prefixes
  const fallbackPrefixes: Record<string, string> = {
    "http://www.w3.org/2001/XMLSchema#": "xsd",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#": "rdf",
    "http://www.w3.org/2000/01/rdf-schema#": "rdfs",
  }

  for (const [namespace, prefix] of Object.entries(fallbackPrefixes)) {
    if (uri.startsWith(namespace)) {
      const localName = uri.slice(namespace.length)
      return `<span class="uri-prefix">${escapeHtml(prefix)}:</span>${escapeHtml(localName)}`
    }
  }

  // No match - return full URI in angle brackets
  return `&lt;${escapeHtml(uri)}&gt;`
}

/**
 * Format an RDF term as HTML, with literal suffixes styled separately.
 */
export function formatRDFTerm(term: RDFTerm, prefixes?: PrefixMap): string {
  switch (term.type) {
    case "uri":
      return `<span class="uri">${abbreviateUri(term.value, prefixes)}</span>`
    case "literal": {
      const value = `"${escapeHtml(term.value)}"`
      if (term["xml:lang"]) {
        return `<span class="literal">${value}<span class="literal-suffix">@${escapeHtml(term["xml:lang"])}</span></span>`
      }
      if (term.datatype && term.datatype !== XSD_STRING) {
        const datatype = abbreviateUri(term.datatype, prefixes)
        return `<span class="literal">${value}<span class="literal-suffix">^^${datatype}</span></span>`
      }
      return `<span class="literal">${value}</span>`
    }
    case "bnode":
      return `<span class="bnode">_:${escapeHtml(term.value)}</span>`
    default:
      return `<span>${escapeHtml(term.value)}</span>`
  }
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}
